import { changeDatacenterColor } from './ngrx/reducers/util/map/data-center.util';
import { Injectable } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { DatacenterV2 } from './model/datacenter-v2';
import { DataCentersService } from './service/data-centers.service';
import { Nestable, CircleData } from './model/summary';
import { Actions, ofType } from '@ngrx/effects';
import { datacenterActions, mapConfigActions } from './ngrx/actions';
import { filter, take } from 'rxjs';
import * as turf from '@turf/turf';
import {
  MARKET_CIRCLE_FILL_LAYER_ID,
  MARKET_CIRCLE_LAYER_ID,
  MARKET_LABEL_LAYER_ID,
  MARKET_LABEL_SOURCE_TYPE,
  MARKET_LINES_SOURCE_TYPE,
  MARKERS_SOURCE_ID
} from './consts/map.constants';
import { createMarketBuffer, processMarketCircleData } from './util/market-utils';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';

const MARKETS_SOURCE_ID = 'market-areas-source';

@Injectable()
export class MarketsHandler {

  private map: mapboxgl.Map;
  private isObservingChanges = false;

  constructor(
    private dataCentersService: DataCentersService,
    private actions$: Actions,
    private store: Store
  ) {}

  public onDatacentersReceive(map: mapboxgl.Map, datacenters: DatacenterV2[]) {
    this.map = map;
    this.observeDatacenterChanges(); // Start observing changes

    this.updateMarketAreas(map, datacenters);
  }

  /**
   * Updates market areas with the given datacenters
   * Groups by 'area' field (Market Area) - requires both 'market' and 'area' fields
   * Creates dashed circles that dynamically adjust to cover all datacenters in each area
   */
  private updateMarketAreas(map: mapboxgl.Map, datacenters: DatacenterV2[]) {
    const extensibleDatacenters = datacenters.map(dc => ({...dc}));
    // Get market areas grouped by 'area' field, independent of providers
    const marketAreas = this.dataCentersService.getSimpleMarketsData(extensibleDatacenters as any);
    this.processMarketAreas(marketAreas); // Calculate dynamic radius for each market area
    this.updateMarketSource(map, marketAreas);
    this.addMarketLayers(map);
    this.addEventListeners(map);
  }

  /**
   * Updates market areas when individual datacenters are modified
   * Gets all current datacenters from the map source and combines with changedDatacenters from store
   */
  public onDatacenterModified(map: mapboxgl.Map, modifiedDatacenter: DatacenterV2) {
    // Get all current datacenters from the map source
    const [source, featureCollection] = this.dataCentersService.getFeatureCollectionFromSource(map, MARKERS_SOURCE_ID);
    if (!source || !featureCollection) {
      return;
    }

    // Convert features back to datacenters
    const mapDatacenters: DatacenterV2[] = featureCollection.features.map(feature => feature.properties as DatacenterV2);

    // Get changedDatacenters from store and combine with map datacenters
    const allDatacenters = this.combineDatacentersWithChanges(mapDatacenters);

    // Recalculate and update market areas with all current datacenters
    this.updateMarketAreas(map, allDatacenters);
  }

  /**
   * Combines datacenters from map source with changedDatacenters from store
   * This ensures market circles reflect the latest changes including unsaved datacenters with auxId
   */
  private combineDatacentersWithChanges(mapDatacenters: DatacenterV2[]): DatacenterV2[] {
    let currentState: any;
    this.store.select(state => state).pipe(
      take(1)
    ).subscribe(state => currentState = state);

    const changedDatacenters: DatacenterV2[] = (currentState as any)?.changedDatacenters || [];
    if (!changedDatacenters || changedDatacenters.length === 0) {
      return mapDatacenters;
    }

    // Start with map datacenters
    let combinedDatacenters = [...mapDatacenters];

    // Process each changed datacenter
    for (const changedDc of changedDatacenters) {
      if (changedDc.auxId) {
        // New datacenter with auxId
        if (changedDc.toDelete) {
          // Remove if marked for deletion
          combinedDatacenters = combinedDatacenters.filter(dc => dc.auxId !== changedDc.auxId);
        } else {
          // Add or update
          const existingIndex = combinedDatacenters.findIndex(dc => dc.auxId === changedDc.auxId);
          if (existingIndex >= 0) {
            combinedDatacenters[existingIndex] = changedDc;
          } else {
            combinedDatacenters.push(changedDc);
          }
        }
      } else if (changedDc.id) {
        // Existing datacenter with regular id
        if (changedDc.toDelete) {
          combinedDatacenters = combinedDatacenters.filter(dc => dc.id !== changedDc.id);
        } else {
          const existingIndex = combinedDatacenters.findIndex(dc => dc.id === changedDc.id);
          if (existingIndex >= 0) {
            combinedDatacenters[existingIndex] = changedDc;
          } else {
            combinedDatacenters.push(changedDc);
          }
        }
      }
    }

    return combinedDatacenters;
  }


  private processMarketAreas(markets: Nestable[]) {
    for (const market of markets) {
      processMarketCircleData(market);
    }
  }

  private updateMarketSource(map: mapboxgl.Map, markets: Nestable[]) {
    let geoJsonSource = map.getSource(MARKETS_SOURCE_ID) as any;
    let featureCollection: GeoJSON.FeatureCollection;

    if (!geoJsonSource) {
      featureCollection = this.createEmptyFeatureCollection();
      map.addSource(MARKETS_SOURCE_ID, {
        type: 'geojson',
        data: featureCollection,
      });
      geoJsonSource = map.getSource(MARKETS_SOURCE_ID) as any;
    } else {
      featureCollection = this.createEmptyFeatureCollection();
    }

    // Features
    for (const market of markets.filter(m => m.label && m.circleData)) {
      const circleFeature = createMarketBuffer(market);
      circleFeature.properties = {
        type: MARKET_LINES_SOURCE_TYPE,
        label: market.label,
        marketData: JSON.stringify({
          label: market.label,
          totalDatacenters: market.entireData ? market.entireData.length : 0,
          center: market.circleData.center,
          radius: market.circleData.ratio
        })
      };
      featureCollection.features.push(circleFeature);

      // Calculate label position based on datacenter count
      const datacenterCount = market.entireData ? market.entireData.length : 0;
      let labelCoordinates;

      if (datacenterCount === 1) {
        // For single datacenter, position label below the circle
        const radiusInMiles = market.circleData.ratio + ((market.circleData.ratio * 20) / 100);
        const offsetDistance = Math.max(radiusInMiles * 0.8, 1.5); // At least 1.5 miles below

        // Use Turf.js for precise geospatial calculation - move south (bearing 180°)
        const centerPoint = turf.point(market.circleData.center);
        const labelPoint = turf.destination(centerPoint, offsetDistance, 180, {units: 'miles'});
        labelCoordinates = labelPoint.geometry.coordinates;
      } else {
        // For multiple datacenters, position label in the center of the circle
        labelCoordinates = market.circleData.center;
      }

      const labelFeature: GeoJSON.Feature = {
        type: "Feature",
        geometry: {
          'type': 'Point',
          'coordinates': labelCoordinates // Position below the center
        },
        properties: {
          type: MARKET_LABEL_SOURCE_TYPE,
          label: market.label,
          marketData: JSON.stringify({
            label: market.label,
            totalDatacenters: market.entireData ? market.entireData.length : 0,
            center: market.circleData.center,
            radius: market.circleData.ratio
          })
        }
      };
      featureCollection.features.push(labelFeature);
    }

    geoJsonSource.setData(featureCollection);
  }

  private addMarketLayers(map: mapboxgl.Map) {
    // Check if layers already exist
    if (!map.getLayer(MARKET_CIRCLE_FILL_LAYER_ID)) {
      // Layer for circle fill
      map.addLayer({
        id: MARKET_CIRCLE_FILL_LAYER_ID,
        type: 'fill',
        source: MARKETS_SOURCE_ID,
        filter: ['==', 'type', MARKET_LINES_SOURCE_TYPE],
        paint: {
          'fill-color': '#007cbf',
          'fill-opacity': 0.1
        },
        layout: {
          visibility: 'none' // Hidden by default
        }
      });
    }

    if (!map.getLayer(MARKET_CIRCLE_LAYER_ID)) {
      // Layer for circle lines
      map.addLayer({
        id: MARKET_CIRCLE_LAYER_ID,
        type: 'line',
        source: MARKETS_SOURCE_ID,
        filter: ['==', 'type', MARKET_LINES_SOURCE_TYPE],
        paint: {
          'line-color': '#007cbf',
          'line-width': 2,
          'line-dasharray': [2, 2]
        },
        layout: {
          visibility: 'none' // Hidden by default
        }
      });
    }

    if (!map.getLayer(MARKET_LABEL_LAYER_ID)) {
      // Layer for labels
      map.addLayer({
        id: MARKET_LABEL_LAYER_ID,
        type: 'symbol',
        source: MARKETS_SOURCE_ID,
        filter: ['==', 'type', MARKET_LABEL_SOURCE_TYPE],
        layout: {
          'text-field': ['get', 'label'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-anchor': 'center',
          'text-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0.1, 4,
            2, 5,
            2.5, 6,
            3, 12,
            15, 12
          ],
          visibility: 'none', // Hidden by default
          'text-allow-overlap': false,
          'text-ignore-placement': false
        },
        paint: {
          'text-color': '#7bb9da',
          'text-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            2.5, 0,
            3, 1,
          ],
          'text-halo-width': 3,
          'text-halo-color': 'rgb(255,255,255)',
        }
      });
    }
  }

  private addEventListeners(map: mapboxgl.Map) {
    map.on('mouseenter', MARKET_LABEL_LAYER_ID, () => {
      map.getCanvasContainer().style.cursor = 'pointer';
    });

    map.on('mouseleave', MARKET_LABEL_LAYER_ID, () => {
      map.getCanvasContainer().style.cursor = '';
    });
  }

  private createEmptyFeatureCollection(): GeoJSON.FeatureCollection {
    return {
      type: "FeatureCollection",
      features: []
    };
  }

  /**
   * Observes datacenter changes and updates market areas accordingly
   *
   */
  private observeDatacenterChanges(): void {
    // Prevent multiple observers
    if (this.isObservingChanges) {
      return;
    }

    this.isObservingChanges = true;

    this.actions$.pipe(
      ofType(
        datacenterActions.datacenterModified,
        datacenterActions.removeDatacenter,
        datacenterActions.getDatacentersSuccess,
      ),
      filter(() => !!this.map)
    ).subscribe(action => {
      if ('datacenter' in action) {
        this.onDatacenterModified(this.map, action.datacenter);
      }
      else if ('datacenters' in action) {
        this.updateMarketAreas(this.map, action.datacenters);
      }
    });

    this.store.select(state => (state as any)?.changedDatacenters).pipe(
      filter(() => !!this.map),
    ).subscribe(() => {
      // Call onDatacenterModified without specific datacenter - it will combine all current data
      this.onDatacenterModified(this.map, null as any);
    });
  }
}
